<template>
  <div class="mainQuestionContainer bg-secondary">
    <h2 class="text-2xl font-semibold mb-2">{{ $t(langKey) }}</h2>
    <div class="question-spacer"></div>

    <!-- Affichage des types -->
    <div class="w-full">
        <input
            v-if="type === 'text'"
            v-model="valueLocal"
            :type="type"
            :maxlength="langKey === 'job' ? '100' : undefined"
            class="inputAnswer"
            :placeholder="getInputPlaceholder()"
            @input="validateInput"
        />

        <!-- Input number avec toggle unité pour taille et poids -->
        <div v-else-if="type === 'number'" class="number-input-container">
            <div class="unit-toggle">
                <button 
                    v-if="langKey === 'actualSize' || langKey === 'targetSize'"
                    type="button"
                    @click="toggleHeightUnit"
                    class="unit-btn"
                    :class="{ active: heightUnit === 'cm' }"
                >
                    cm
                </button>
                <button 
                    v-if="langKey === 'actualSize' || langKey === 'targetSize'"
                    type="button"
                    @click="toggleHeightUnit"
                    class="unit-btn"
                    :class="{ active: heightUnit === 'ft' }"
                >
                    ft
                </button>
                <button 
                    v-if="langKey === 'actualWeight' || langKey === 'targetWeight'"
                    type="button"
                    @click="toggleWeightUnit"
                    class="unit-btn"
                    :class="{ active: weightUnit === 'kg' }"
                >
                    kg
                </button>
                <button 
                    v-if="langKey === 'actualWeight' || langKey === 'targetWeight'"
                    type="button"
                    @click="toggleWeightUnit"
                    class="unit-btn"
                    :class="{ active: weightUnit === 'lb' }"
                >
                    lb
                </button>
            </div>
            <input
                v-model="displayValue"
                type="number"
                :min="0"
                :step="getStep()"
                class="inputAnswer"
                :placeholder="getInputPlaceholder()"
                @input="validateInput"
                @keyup.enter="submit"
            />
        </div>

        <!-- Choix d'options (boutons cliquables) -->
        <div v-else-if="type === 'checkbox' || type === 'radio'" class="optionsContainer">
            <div
                v-for="(opt, i) in options"
                :key="i"
                :class="{
                    'option bg-tertiary borderAccent': multipleChoice ? valueLocal.includes(opt.option) : valueLocal === opt.option,
                    'option bg-tertiary': multipleChoice ? !valueLocal.includes(opt.option) : valueLocal !== opt.option,
                    'option-disabled': isOptionDisabled(opt.option)
                }"
                @click="!isOptionDisabled(opt.option) && selectOption(opt.option)"
            >
                <span class="option-text">{{ getOptionMainLabel(opt.option) }}</span>
                <span v-if="getDailyStepsSubLabel(opt.option)" class="option-subtext">{{ getDailyStepsSubLabel(opt.option) }}</span>
                <span v-if="isOptionDisabled(opt.option)" class="disabled-reason">
                    {{ getDisabledReason(opt.option) }}
                </span>
            </div>
        </div>
    </div>

    <!-- Avertissement sportif de haut niveau -->
    <div v-if="showHighLevelAthleteWarning" class="bmi-warning">
      <div class="warning-title">⚠️ Impossible de créer un compte</div>
      <div class="warning-text">
        Cette application n'est pas optimale pour les sportifs de haut niveau. 
        Nous vous recommandons de consulter un nutritionniste sportif spécialisé pour un suivi personnalisé adapté à vos besoins spécifiques.
      </div>
    </div>

    <!-- Avertissement diabète + obésité -->
    <div v-if="showDiabetesWarning" class="bmi-warning">
      <div class="warning-title">⚠️ Impossible de créer un compte</div>
      <div class="warning-text">
        Avec un IMC supérieur à 30 et du diabète, cette application n'est pas adaptée à votre situation. 
        Nous vous recommandons vivement de consulter un professionnel de santé pour un suivi personnalisé.
      </div>
    </div>

    <!-- Avertissement régime flexitarien/pescetarien -->
    <div v-if="showDietWarning" class="bmi-warning">
      <div class="warning-title">⚠️ Impossible de créer un compte</div>
      <div class="warning-text">
        Cette application n'est actuellement pas adaptée aux régimes flexitariens et pescetariens. 
        Nous travaillons pour intégrer ces options prochainement.
      </div>
    </div>

    <!-- Avertissement plus de 7 séances par semaine -->
    <div v-if="showIntensiveWorkoutWarning" class="workout-warning">
      <div class="warning-title">⚠️ Attention</div>
      <div class="warning-text">
        Avec plus de 7 séances de sport par semaine, il se peut que l'application ne soit pas adaptée à 100% à vos besoins très spécifiques. 
        Un suivi nutritionnel personnalisé pourrait être plus approprié.
      </div>
    </div>

    <!-- Avertissement repas incompatibles avec objectif poids -->
    <div v-if="showMealsWarning" class="workout-warning">
      <div class="warning-title">⚠️ Attention</div>
      <div class="warning-text">
        {{ mealsWarningMessage }}
      </div>
    </div>

    <!-- Navigation -->
    <div class="mt-5 actionContainer">
      <button 
        v-if="question > 1" 
        @click="previousStep" 
        type="button"
        class="previousButton button-base"
      >
        Retour
      </button>
      <button 
        v-if="(multipleChoice && type !== 'text' && type !== 'number') || type === 'number' || type === 'text'" 
        @click="submit" 
        type="button"
        class="nextButton bg-accent button-base"
        :class="{ 'nextButton-disabled': isSubmitDisabled }"
        :disabled="isSubmitDisabled"
      >
        Suivant
      </button>
    </div>
  </div>

  <v-snackbar v-model="showError" color="error" top right :timeout="3000">
    {{ errorMessage }}
  </v-snackbar>
</template>

<script>
export default {
    name: 'RegisterQuestionsBlock',
    props: {
        langKey: String,
        question: [String, Number],
        options: Array,
        type: String,
        multipleChoice: Boolean,
        value: [String, Number, Array],
        allResponses: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            valueLocal: this.multipleChoice ? [] : '',
            errorMessage: '',
            showError: false,
            calculatedBMI: 0,
            heightUnit: 'cm',
            weightUnit: 'kg',
            displayValue: '',
        };
    },
    computed: {
        showHelperText() {
            return this.type === 'select';
        },
        currentBMI() {
            const weight = parseFloat(this.allResponses.actualWeight);
            const heightCm = parseFloat(this.allResponses.actualSize);
            
            if (weight && heightCm && heightCm > 0) {
                const heightM = heightCm / 100;
                return weight / (heightM * heightM);
            }
            return 0;
        },
        showHighLevelAthleteWarning() {
            return this.langKey === 'isHighLevelAthlete' && this.valueLocal === 'yes';
        },
        showDiabetesWarning() {
            if (this.langKey !== 'medicalConditions') return false;
            
            const hasDiabetes = Array.isArray(this.valueLocal) && this.valueLocal.includes('diabetes');
            
            return hasDiabetes && this.currentBMI > 30;
        },
        showDietWarning() {
            if (this.langKey !== 'diet') return false;
            
            return Array.isArray(this.valueLocal) && 
                   (this.valueLocal.includes('flexitarian') || this.valueLocal.includes('pescatarian'));
        },
        showIntensiveWorkoutWarning() {
            return this.langKey === 'weeklyWorkouts' && this.valueLocal === 'moreThan7';
        },
        showMealsWarning() {
            if (this.langKey !== 'takenMealsObjectives') return false;
            
            const weightObjective = this.allResponses.weightObjective;
            const currentMeals = this.allResponses.takenMeals || [];
            const futureMeals = Array.isArray(this.valueLocal) ? this.valueLocal : [];
            
            if (!weightObjective || currentMeals.length === 0 || futureMeals.length === 0) return false;
            
            if (weightObjective === 'gainWeight' && futureMeals.length < currentMeals.length) {
                return true;
            }
            
            if (weightObjective === 'loseWeight' && futureMeals.length > currentMeals.length) {
                return true;
            }
            
            return false;
        },
        mealsWarningMessage() {
            const weightObjective = this.allResponses.weightObjective;
            
            if (weightObjective === 'gainWeight') {
                return "Vous souhaitez prendre du poids mais prévoyez de faire moins de repas. Cela pourrait rendre votre objectif plus difficile à atteindre.";
            }
            
            if (weightObjective === 'loseWeight') {
                return "Vous souhaitez perdre du poids mais prévoyez de faire plus de repas. Cela pourrait rendre votre objectif plus difficile à atteindre.";
            }
            
            return "";
        },
        isSubmitDisabled() {
            return this.showDiabetesWarning || this.showHighLevelAthleteWarning || this.showDietWarning;
        }
    },
    watch: {
        langKey(newKey, oldKey) {
            if (newKey !== oldKey) {
                this.resetValue();
            }
        },
        value(newVal) {
            if (newVal !== undefined && newVal !== null) {
                this.valueLocal = this.multipleChoice ? (newVal || []) : (newVal || '');
                this.updateDisplayValue();
            }
        },
    },
    mounted() {
        this.resetValue();
    },
    methods: {
        resetValue() {
            if (this.value !== undefined && this.value !== null) {
                this.valueLocal = this.multipleChoice ? (this.value || []) : (this.value || '');
            } else {
                this.valueLocal = this.multipleChoice ? [] : '';
            }
            this.updateDisplayValue();
        },
        getStep() {
            if (this.langKey === 'actualSize' || this.langKey === 'targetSize') {
                return this.heightUnit === 'cm' ? '0.1' : '0.01';
            }
            if (this.langKey === 'actualWeight' || this.langKey === 'targetWeight') {
                return this.weightUnit === 'kg' ? '0.1' : '0.1';
            }
            return '0.1';
        },
        toggleHeightUnit() {
            this.heightUnit = this.heightUnit === 'cm' ? 'ft' : 'cm';
            this.updateDisplayValue();
        },
        toggleWeightUnit() {
            this.weightUnit = this.weightUnit === 'kg' ? 'lb' : 'kg';
            this.updateDisplayValue();
        },
        updateDisplayValue() {
            if (!this.valueLocal || this.type !== 'number') {
                this.displayValue = this.valueLocal;
                return;
            }
            
            const numValue = parseFloat(this.valueLocal);
            
            if (this.langKey === 'actualSize' || this.langKey === 'targetSize') {
                if (this.heightUnit === 'ft') {
                    const totalInches = numValue / 2.54;
                    const feet = Math.floor(totalInches / 12);
                    const inches = (totalInches % 12).toFixed(1);
                    this.displayValue = `${feet}.${inches.replace('.', '')}`;
                } else {
                    this.displayValue = numValue.toString();
                }
            } else if (this.langKey === 'actualWeight' || this.langKey === 'targetWeight') {
                if (this.weightUnit === 'lb') {
                    this.displayValue = (numValue * 2.20462).toFixed(1);
                } else {
                    this.displayValue = numValue.toString();
                }
            } else {
                this.displayValue = this.valueLocal;
            }
        },
        convertDisplayToStored() {
            if (!this.displayValue || this.type !== 'number') {
                this.valueLocal = this.displayValue;
                return;
            }
            
            const numValue = parseFloat(this.displayValue);
            
            if (this.langKey === 'actualSize' || this.langKey === 'targetSize') {
                if (this.heightUnit === 'ft') {
                    const feet = Math.floor(numValue);
                    const inches = (numValue - feet) * 100;
                    const totalInches = (feet * 12) + inches;
                    // CORRECTION: Stocker comme nombre, pas comme chaîne
                    this.valueLocal = parseFloat((totalInches * 2.54).toFixed(1));
                } else {
                    this.valueLocal = numValue;
                }
            } else if (this.langKey === 'actualWeight' || this.langKey === 'targetWeight') {
                if (this.weightUnit === 'lb') {
                    // CORRECTION: Stocker comme nombre, pas comme chaîne
                    this.valueLocal = parseFloat((numValue / 2.20462).toFixed(1));
                } else {
                    this.valueLocal = numValue;
                }
            } else {
                this.valueLocal = parseFloat(this.displayValue);
            }
        },
        getInputPlaceholder() {
            if (this.type === 'number') {
                if (this.langKey === 'dailySteps') {
                    return 'Nombre de pas par jour';
                }
                if (this.langKey === 'actualSize' || this.langKey === 'targetSize') {
                    return this.heightUnit === 'cm' ? 'Taille en cm' : 'Taille en pieds (ex: 5.9)';
                }
                if (this.langKey === 'actualWeight' || this.langKey === 'targetWeight') {
                    return this.weightUnit === 'kg' ? 'Poids en kg' : 'Poids en lb';
                }
                return 'Entrez un nombre positif';
            }
            if (this.langKey === 'job') {
                return 'Entrez votre métier (100 caractères max)';
            }
            return 'Entrez votre réponse';
        },
        getOptionLabel(option) {
            if (this.langKey === 'weeklyWorkouts' && !isNaN(option)) {
                return option;
            }
            if (option === 'moreThan7') {
                return '+ de 7';
            }
            return this.$t(option);
        },
        isOptionDisabled(option) {
            if (this.langKey === 'weightObjective') {
                const bmi = this.currentBMI;
                
                if (bmi > 0 && bmi < 15.5) {
                    return option === 'maintainWeight' || option === 'loseWeight';
                }
            }
            
            return false;
        },
        getDisabledReason(option) {
            if (this.langKey === 'weightObjective' && this.currentBMI > 0 && this.currentBMI < 15.5) {
                if (option === 'maintainWeight' || option === 'loseWeight') {
                    return '(IMC trop bas)';
                }
            }
            return '';
        },
        validateInput(event) {
            if (this.type === 'number') {
                const value = parseFloat(event.target.value);
                if (value < 0) {
                    this.displayValue = '';
                    event.target.value = '';
                }
            } else if (this.type === 'text' && this.langKey === 'job') {
                if (event.target.value.length > 100) {
                    this.valueLocal = event.target.value.substring(0, 100);
                    event.target.value = this.valueLocal;
                }
            }
        },
        submit() {
            if (this.isSubmitDisabled) {
                this.errorMessage = 'Impossible de continuer : l\'application n\'est pas adaptée à votre situation';
                this.showError = true;
                return;
            }

            // Convertir la valeur affichée en valeur stockée pour les nombres
            if (this.type === 'number') {
                this.convertDisplayToStored();
            }

            if (!this.valueLocal || (Array.isArray(this.valueLocal) && this.valueLocal.length === 0)) {
                this.errorMessage = 'Veuillez répondre à la question';
                this.showError = true;
                return;
            }

            if (this.langKey === 'job') {
                if (this.valueLocal.trim().length === 0) {
                    this.errorMessage = 'Veuillez entrer votre métier';
                    this.showError = true;
                    return;
                }
                if (this.valueLocal.length > 100) {
                    this.errorMessage = 'Le métier ne peut pas dépasser 100 caractères';
                    this.showError = true;
                    return;
                }
            }

            if (this.type === 'number') {
                const numValue = parseFloat(this.valueLocal);
                
                if (isNaN(numValue) || numValue < 0) {
                    this.errorMessage = 'Veuillez entrer un nombre valide et positif';
                    this.showError = true;
                    return;
                }

                if (this.langKey === 'actualWeight' && (numValue < 20 || numValue > 300)) {
                    this.errorMessage = 'Veuillez entrer un poids réaliste (entre 20 et 300 kg)';
                    this.showError = true;
                    return;
                }

                if (this.langKey === 'actualSize' && (numValue < 50 || numValue > 250)) {
                    this.errorMessage = 'Veuillez entrer une taille réaliste (entre 50 et 250 cm)';
                    this.showError = true;
                    return;
                }

                if (this.langKey === 'targetWeight') {
                    // CORRECTION: Utiliser allResponses au lieu de this.$parent.responses
                    const actualWeight = parseFloat(this.allResponses.actualWeight);
                    const weightObjective = this.allResponses.weightObjective;
                    const heightCm = parseFloat(this.allResponses.actualSize);
                    
                    if (weightObjective === 'loseWeight' && numValue >= actualWeight) {
                        this.errorMessage = 'Votre poids cible doit être inférieur à votre poids actuel pour perdre du poids';
                        this.showError = true;
                        return;
                    }

                    if (weightObjective === 'gainWeight' && numValue <= actualWeight) {
                        this.errorMessage = 'Votre poids cible doit être supérieur à votre poids actuel pour prendre du poids';
                        this.showError = true;
                        return;
                    }

                    if (heightCm && heightCm > 0) {
                        const heightM = heightCm / 100;
                        const targetBMI = numValue / (heightM * heightM);
                                                
                        if (targetBMI < 15.5) {
                            this.errorMessage = `Ce poids cible donnerait un IMC de ${targetBMI.toFixed(1)}, ce qui est trop bas (minimum 15.5)`;
                            this.showError = true;
                            return;
                        }
                    }

                    if (numValue < 20 || numValue > 300) {
                        this.errorMessage = 'Veuillez entrer un poids cible réaliste (entre 20 et 300 kg)';
                        this.showError = true;
                        return;
                    }
                }
            }

            if (this.langKey === 'weeklyWorkouts' || this.langKey === 'dailySteps' || this.langKey === 'activityLevel') {
                const dailySteps = this.langKey === 'dailySteps' ? this.valueLocal : this.allResponses.dailySteps;
                const activityLevel = this.langKey === 'activityLevel' ? this.valueLocal : this.allResponses.activityLevel;
                const weeklyWorkouts = this.langKey === 'weeklyWorkouts' ? this.valueLocal : this.allResponses.weeklyWorkouts;
                
                if (dailySteps && activityLevel && weeklyWorkouts) {
                    const nap = this.calculateNAP(dailySteps, activityLevel, weeklyWorkouts);
                    this.$emit('update', { key: 'NAP', value: nap });
                }
            }
            
            this.$emit('update', { key: this.langKey, value: this.valueLocal });
            this.proceedToNext();
            
            this.errorMessage = '';
            this.showError = false;
        },
        calculateNAP(dailySteps, activityLevel, weeklyWorkouts) {
            let nap = 0;
            
            const highSteps = dailySteps === 'from7000to10000' || dailySteps === 'moreThan10000';
            
            if (!highSteps) {
                switch (activityLevel) {
                    case 'sedentary': nap = 1.4; break;
                    case 'lightlyActive': nap = 1.6; break;
                    case 'moderatelyActive': nap = 1.8; break;
                    case 'veryActive': nap = 2.2; break;
                    default: nap = 1.4;
                }
            } else {
                switch (activityLevel) {
                    case 'sedentary': nap = 1.5; break;
                    case 'lightlyActive': nap = 1.7; break;
                    case 'moderatelyActive': nap = 1.8; break;
                    case 'veryActive': nap = 2.2; break;
                    default: nap = 1.5;
                }
            }
            
            const workouts = weeklyWorkouts === 'moreThan7' ? 8 : parseInt(weeklyWorkouts) || 0;
            
            if (workouts < 2) nap += 0;
            else if (workouts >= 2 && workouts < 3) nap += 0.05;
            else if (workouts >= 3 && workouts < 4) nap += 0.1;
            else if (workouts >= 4 && workouts <= 6) nap += 0.15;
            else if (workouts > 6) nap += 0.2;
            
            return Math.round(nap * 100) / 100;
        },
        proceedToNext() {
            if (!this.multipleChoice && this.type !== 'text' && this.type !== 'number') {
                setTimeout(() => {
                    this.$emit('nextStep');
                }, 200);
            } else {
                this.$emit('nextStep');
            }
        },
        selectOption(option) {
            if (this.type === 'checkbox') {
                if (!Array.isArray(this.valueLocal)) this.valueLocal = [];
                const index = this.valueLocal.indexOf(option);
                if (index > -1) {
                    this.valueLocal.splice(index, 1);
                } else {
                    this.valueLocal.push(option);
                }
            } else {
                this.valueLocal = option;
                if (!this.multipleChoice) this.submit();
            }
        },
        getOptionMainLabel(option) {
            const mainLabels = {
                'lessThan3500': 'Moins de 3 500 pas',
                'from3500to7000': '3 500 à 7 000 pas',
                'from7000to10000': '7 000 à 10 000 pas',
                'moreThan10000': 'Plus de 10 000 pas',
            };
            return mainLabels[option] || this.getOptionLabel(option);
        },
        getDailyStepsSubLabel(option) {
            if (this.langKey !== 'dailySteps') return null;
            const subLabels = {
                'lessThan3500': 'moins de 30 min. de marche par jour',
                'from3500to7000': '30 min. à 1h de marche par jour',
                'from7000to10000': '1h à 1h30 de marche par jour',
                'moreThan10000': 'plus de 1h30 de marche par jour',
            };
            return subLabels[option] || null;
        },
        previousStep() {
            this.$emit('previousStep');
        },
    },
};
</script>

<style>
  .option:hover {
    cursor: pointer;
  }

  .option:hover.option-disabled {
    border: 3px solid transparent !important;
    cursor: not-allowed !important;
  }

  .borderAccent {
    border: 3px solid #111111 !important;
  }
</style>

<style scoped>
    .mainQuestionContainer {
        padding: 2em;
        width: 90%;
        max-width: 600px;
        display: flex;
        flex-direction: column;
        align-items: center;
        border-radius: 2em;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        color: #111111 !important;
    }

    .actionContainer {
        display: flex;
        justify-content: center;
        margin-top: 3em;
        gap: 12px;
        width: 100%;
    }

    .nextButton:hover:not(:disabled):not(.nextButton-disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .question-spacer { height: 20px; }

    .optionsContainer {
        display: flex;
        flex-direction: column;
        gap: 1em;
        width: 100%;
    }

    .option {
        padding: 1em 2em;
        border-radius: 0.5em;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 3px solid transparent;
        transition: all 0.2s ease;
        position: relative;
    }

    .option-disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
        background-color: #6b7280 !important;
    }

    .disabled-reason {
        font-size: 0.8em;
        color: #ef4444;
        margin-top: 0.25em;
        font-style: italic;
    }

    .number-input-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.5em;
    }

    .unit-toggle {
        display: flex;
        gap: 0.5em;
        justify-content: center;
    }

    .unit-btn {
        padding: 0.5em 1.5em;
        border: 2px solid #ccc;
        background-color: #f5f5f5;
        border-radius: 0.5em;
        cursor: pointer;
        transition: all 0.2s ease;
        font-weight: 600;
    }

    .unit-btn.active {
        background-color: #d6e2f2;
        color: #0f1115;
        border-color: #111111;
    }

    .unit-btn:hover {
        transform: scale(1.05);
    }

    .inputAnswer {
        width: 100%;
        padding: 0.75em;
        border-radius: 0.5em;
        border: 2px solid #ccc;
        font-size: 1em;
        transition: border-color 0.3s ease;
        color: #111111;
    }

    .inputAnswer:focus {
        outline: none;
        border-color: #111111;
    }

    .error-message {
        animation: shake 0.5s;
    }

    .bmi-warning {
        margin-top: 1.5em;
        padding: 1.5em;
        background-color: #fee;
        border: 2px solid #ef4444;
        border-radius: 0.75em;
        width: 100%;
    }

    .workout-warning {
        margin-top: 1.5em;
        padding: 1.5em;
        background-color: #fef3c7;
        border: 2px solid #f59e0b;
        border-radius: 0.75em;
        width: 100%;
    }

    .warning-title {
        color: #b91c1c;
        font-weight: 600;
        font-size: 1.1em;
        margin-bottom: 0.5em;
    }

    .workout-warning .warning-title {
        color: #d97706;
    }

    .warning-text {
        color: #dc2626;
        line-height: 1.5;
    }

    .workout-warning .warning-text {
        color: #d97706;
    }

    .option-text {
        text-align: center;
        font-weight: 500;
    }

    .option-subtext {
        text-align: center;
        font-weight: 400;
        font-size: 0.85em;
        color: #6b7280;
        margin-top: 2px;
    }

    .button-base {
        font-weight: bold;
        padding: 0.75em 2em;
        border-radius: 999px;
        transition: all 0.3s ease;
        cursor: pointer;
        border: none;
    }

    .nextButton {
        color: #ffffff !important;
        background: #1A1615 !important;
        box-shadow: none !important;
    }

    .nextButton:hover:not(:disabled):not(.nextButton-disabled) {
        transform: translateY(-2px);
        opacity: 0.85;
    }

    .nextButton-disabled {
        background-color: #9ca3af !important;
        cursor: not-allowed !important;
        opacity: 0.6;
    }

    .previousButton {
        background: transparent !important;
        color: #1A1615 !important;
        border: 2px solid #1A1615 !important;
        box-shadow: none !important;
    }

    .previousButton:hover {
        background: rgba(26,22,21,0.06) !important;
        transform: translateY(-2px);
        opacity: 0.85;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-10px); }
        75% { transform: translateX(10px); }
    }

    @media only screen and (min-width: 768px) {
        .mainQuestionContainer {
            width: 60%;
        }
    }

    @media only screen and (min-width: 1024px) {
        .mainQuestionContainer {
            width: 40%;
        }
    }

    @media only screen and (min-width: 1500px) {
        .mainQuestionContainer {
            width: 30%;
        }
    }
</style>
